import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import StageFrame from './StageFrame';
import { useCycle } from './useCycle';

const OLD_PATH = 'M16 78 C 70 18, 110 110, 160 64 S 230 20, 304 86';
const NEW_PATH = 'M16 78 C 78 84, 120 48, 168 56 S 250 92, 304 52';

const Ticket: React.FC<{ en: string; zh: string; late: boolean; label: string; status: string }> = ({
  en,
  zh,
  late,
  label,
  status,
}) => (
  <div className={`rounded-xl border px-3 py-3 ${late ? 'border-coral/40 bg-coral-dim' : 'border-mint/40 bg-mint-dim'}`}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-mono text-[0.65rem] uppercase tracking-wide text-stamp">{label}</p>
      <span className={`font-mono text-[0.65rem] ${late ? 'text-coral' : 'text-mint'}`}>{status}</span>
    </div>
    <p className="mt-2 text-sm text-paper">{en}</p>
    <p className="text-sm text-paper/90">{zh}</p>
  </div>
);

const RouteMap: React.FC<{ solved: boolean; reduce: boolean }> = ({ solved, reduce }) => (
  <svg viewBox="0 0 320 130" className="h-40 w-full" role="img" aria-hidden="true">
    <path d={OLD_PATH} fill="none" stroke="#ff4d6a" strokeWidth="2.4" strokeDasharray="5 5" opacity={solved ? 0.25 : 0.95} />
    <motion.path
      d={NEW_PATH}
      fill="none"
      stroke="#3dffc5"
      strokeWidth="2.8"
      initial={reduce ? false : { pathLength: solved ? 0 : 1, opacity: solved ? 0 : 0.2 }}
      animate={{ pathLength: solved || reduce ? 1 : 0.15, opacity: solved || reduce ? 1 : 0.25 }}
      transition={{ duration: reduce ? 0 : 1.1, ease: [0.22, 1, 0.36, 1] }}
    />
    <circle cx="16" cy="78" r="4" fill="#e8a317" />
    <circle cx="304" cy={solved ? 52 : 86} r="5" fill={solved ? '#3dffc5' : '#ff4d6a'} />
    {!solved && <circle cx="168" cy="70" r="6" fill="#ff4d6a" />}
  </svg>
);

const RetailFlow: React.FC = () => {
  const { t } = useTranslation();
  const { phase, reduce, replay } = useCycle(6800);
  const solved = phase === 'solution';
  const problemCaption = t('anim.retail.problemCaption');
  const solutionCaption = t('anim.retail.solutionCaption');

  const board = (isSolved: boolean) => (
    <div className="grid items-center gap-4 md:grid-cols-[0.9fr_1.1fr]">
      <div className="space-y-3">
        <Ticket
          label={t('anim.retail.ticket')}
          status={isSolved ? t('anim.retail.healthy') : t('anim.retail.late')}
          late={!isSolved}
          en={isSolved ? t('anim.retail.okEn') : t('anim.retail.lateEn')}
          zh={isSolved ? t('anim.retail.okZh') : t('anim.retail.lateZh')}
        />
        <Ticket
          label="EN · 中文"
          status={isSolved ? t('anim.retail.healthy') : t('anim.retail.late')}
          late={!isSolved}
          en={isSolved ? t('anim.retail.holdEn') : t('anim.retail.pileEn')}
          zh={isSolved ? t('anim.retail.holdZh') : t('anim.retail.pileZh')}
        />
      </div>
      <RouteMap solved={isSolved} reduce={reduce} />
    </div>
  );

  return (
    <StageFrame
      kicker={t('anim.retail.kicker')}
      phase={reduce ? 'solution' : phase}
      caption={`${problemCaption} ${solutionCaption}`}
      onReplay={replay}
    >
      {reduce ? (
        <div className="space-y-8">
          {board(false)}
          {board(true)}
        </div>
      ) : (
        board(solved)
      )}
    </StageFrame>
  );
};

export default RetailFlow;
